import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import styles from './UIGameChat.module.scss';
import feedStyles from './UIGameChatContainer/UIGameChatContainer.module.scss';
import { UIGameChatContainer } from './UIGameChatContainer/UIGameChatContainer.tsx';
import { UIGameFeedMessage } from '../UIGameFeedMessage/UIGameFeedMessage.tsx';
import { GameChatType } from '@colonist/game-states';
import { createFeedMessageDataForGameChatData } from '../UIGameFeedMessage/FeedMessageDataChats.ts';
import { UIGameChatDebugMessage } from './UIGameChatDebugMessage.tsx';
import { useTranslation } from '@colonist/ui';
import { AudioManager, AudioType } from '@colonist/audio';
import { UIGameFeedVirtualScrollContainer } from '../UIGameFeedVirtualScrollContainer/UIGameFeedVirtualScrollContainer.tsx';
import clsx from 'clsx';
import IconSend from '@static/images/icon_send.svg';
import IconDownArrowBlack from '@static/images/icon_arrow_down_black.svg';
import { useGameStore } from '../../../store/GameStoreContext.ts';
import { useHasOnlyOneHumanPlayer } from '../../../store/selectors/useHasOnlyOneHumanPlayer.ts';
import { usePlayerValidators } from '../../../store/selectors/usePlayerValidators.ts';
import { useIsReplay } from '../../../store/selectors/useIsReplay.ts';
const UIGameChatFeedMessage = /*#__PURE__*/ memo(function UIGameChatFeedMessage({ gameValidator, gameChatData }) {
    const feedMessageData = createFeedMessageDataForGameChatData(gameValidator, gameChatData);
    return /*#__PURE__*/ React.createElement(UIGameFeedMessage, {
        message: feedMessageData
    });
});
const FIRST_MESSAGE_LOCAL_KEY = 'local0';
const monitoringChatData = {
    type: GameChatType.SystemMessage,
    message: `Chat is being monitored. Please be respectful.`
};
// TODO: @Dang remove this when SocketWebActionController fully migrated to React.
let globalAddSpectatorMessage;
export function addGlobalSpectatorMessage(data) {
    if (globalAddSpectatorMessage) globalAddSpectatorMessage(data);
}
export function UIGameChat({ isChatVisible, isMinimizeable, gameValidator, backgroundColor }) {
    const { t } = useTranslation();
    const { sendChatMessage } = useGameStore((state)=>state.gameSendEvent);
    const isBotGame = useGameStore((state)=>state.gameClientConfig.isBotGame);
    const gameChatState = useGameStore((state)=>state.gameState.gameChatState);
    const { getPlayerValidator } = usePlayerValidators();
    const isPrivateGame = useGameStore((state)=>state.gameSettings.isPrivateGame);
    const isMyColorSpectator = useGameStore((state)=>state.isMyColorSpectator);
    const [isMinimized, setIsMinimized] = useState(isBotGame);
    const [wasChatMonitoringWarningShown, setWasChatMonitoringWarningShown] = useState(false);
    const inputRef = useRef(null);
    const [localMessages, setLocalMessages] = useState({});
    const mutedPlayers = useGameStore((state)=>state.gameMute.mutedPlayers);
    const hasOnlyOneHumanPlayer = useHasOnlyOneHumanPlayer();
    const isReplay = useIsReplay();
    globalAddSpectatorMessage = (data)=>{
        const messageElement = /*#__PURE__*/ React.createElement(UIGameChatFeedMessage, {
            gameChatData: data,
            gameValidator: gameValidator
        });
        sendLocalMessage(messageElement);
    };
    const handleSubmit = (event)=>{
        var _inputRef_current;
        event.preventDefault();
        var _inputRef_current_value_trim;
        const message = (_inputRef_current_value_trim = (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.value.trim()) !== null && _inputRef_current_value_trim !== void 0 ? _inputRef_current_value_trim : '';
        if (message === '') return;
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        if (message === '/debug') sendLocalMessage(/*#__PURE__*/ React.createElement(UIGameChatDebugMessage, null));
        else sendChatMessage(message);
    };
    const sendLocalMessage = (message)=>{
        var _Object_keys_at;
        const key = (_Object_keys_at = Object.keys(gameChatState).at(-1)) !== null && _Object_keys_at !== void 0 ? _Object_keys_at : FIRST_MESSAGE_LOCAL_KEY;
        var _localMessages_key;
        const localMessagesAtKey = (_localMessages_key = localMessages[key]) !== null && _localMessages_key !== void 0 ? _localMessages_key : [];
        const messageWithKey = /*#__PURE__*/ React.createElement("div", {
            key: `${key}-${localMessagesAtKey.length}`
        }, message);
        setLocalMessages({
            ...localMessages,
            [key]: [
                ...localMessagesAtKey,
                messageWithKey
            ]
        });
    };
    useEffect(()=>{
        if (isMyColorSpectator) return;
        if (isMinimized) return;
        if (isPrivateGame) return;
        if (wasChatMonitoringWarningShown) return;
        if (hasOnlyOneHumanPlayer === true) return;
        setWasChatMonitoringWarningShown(true);
        sendLocalMessage(/*#__PURE__*/ React.createElement(UIGameChatFeedMessage, {
            gameChatData: monitoringChatData,
            gameValidator: gameValidator
        }));
    }, [
        isMinimized,
        wasChatMonitoringWarningShown
    ]);
    const messages = useMemo(()=>{
        const msgs = [];
        if (localMessages[FIRST_MESSAGE_LOCAL_KEY] !== undefined) msgs.push(...localMessages[FIRST_MESSAGE_LOCAL_KEY]);
        for(const key in gameChatState){
            const gameChatData = gameChatState[key].text;
            if (gameChatData.type === GameChatType.Message && gameChatData.from != null) {
                var _getPlayerValidator;
                const username = (_getPlayerValidator = getPlayerValidator(gameChatData.from)) === null || _getPlayerValidator === void 0 ? void 0 : _getPlayerValidator.username;
                if (username != null && mutedPlayers.includes(username)) continue;
            }
            msgs.push(/*#__PURE__*/ React.createElement(UIGameChatFeedMessage, {
                gameChatData: gameChatData,
                gameValidator: gameValidator,
                key: key
            }));
            const localMessagesAtKey = localMessages[key];
            if (localMessagesAtKey !== undefined) msgs.push(...localMessagesAtKey);
        }
        return msgs;
    }, [
        gameChatState,
        localMessages,
        mutedPlayers
    ]);
    const numberOfMessages = Object.keys(messages).length;
    const prevMessagesCountRef = React.useRef(numberOfMessages);
    const hasNewMessages = numberOfMessages > prevMessagesCountRef.current;
    useEffect(()=>{
        if (hasNewMessages) {
            AudioManager.play(AudioType.MessageNotification);
        }
        prevMessagesCountRef.current = numberOfMessages;
    }, [
        hasNewMessages,
        numberOfMessages,
        mutedPlayers
    ]);
    const [numberOfReadMessages, setNumberOfReadMessages] = useState(numberOfMessages);
    const numberOfUnreadMessages = useMemo(()=>numberOfMessages - numberOfReadMessages, [
        numberOfReadMessages,
        numberOfMessages
    ]);
    useEffect(()=>{
        if (isMinimized) return;
        setNumberOfReadMessages(numberOfMessages);
    }, [
        isMinimized,
        numberOfMessages
    ]);
    const showMinimizedNotification = numberOfUnreadMessages > 0;
    const isAfterLiveGame = useGameStore((state)=>state.gameEnd.isAfterLiveGame);
    const isChatInputDisabled = isReplay && !isAfterLiveGame;
    if (!isChatVisible) return null;
    return /*#__PURE__*/ React.createElement(UIGameChatContainer, {
        isMinimized: isMinimized && isMinimizeable,
        minimizedTitle: t('strings:imgAlts.Chat'),
        setIsMinimized: setIsMinimized,
        showMinimizedNotification: showMinimizedNotification,
        backgroundColor: backgroundColor
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.chatContainer
    }, /*#__PURE__*/ React.createElement(UIGameFeedVirtualScrollContainer, {
        autoScrollToBottom: true
    }, messages), /*#__PURE__*/ React.createElement("div", {
        className: styles.chatBottomContainer
    }, /*#__PURE__*/ React.createElement("form", {
        className: styles.formChat,
        onSubmit: handleSubmit
    }, /*#__PURE__*/ React.createElement("input", {
        className: clsx(styles.inputText, {
            [styles.blue]: backgroundColor === 'blue'
        }),
        "data-testid": "game-chat-input",
        maxLength: 200,
        placeholder: t('strings:homePage.header.sendMessage'),
        ref: inputRef,
        disabled: isChatInputDisabled,
        type: "text"
    }), /*#__PURE__*/ React.createElement("button", {
        className: clsx(styles.buttonSubmit, {
            [styles.blue]: backgroundColor === 'blue',
            [styles.beige]: backgroundColor === 'beige'
        }),
        disabled: isChatInputDisabled,
        "data-testid": "game-chat-submit"
    }, /*#__PURE__*/ React.createElement("img", {
        src: IconSend
    }))), isMinimizeable && /*#__PURE__*/ React.createElement("div", {
        className: feedStyles.chatMinimizeButton,
        onClick: ()=>setIsMinimized(true)
    }, /*#__PURE__*/ React.createElement("img", {
        src: IconDownArrowBlack
    })))));
}
